import React, { Dispatch, SetStateAction } from 'react';

import { TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { ReactComponent as ColumnSortIcon } from 'assets/icons/ColumnSort.svg';
import { ReactComponent as ColumnSortAscIcon } from 'assets/icons/ColumnSortActiveAsc.svg';
import { ReactComponent as ColumnSortDescIcon } from 'assets/icons/ColumnSortActiveDesc.svg';
import { Order } from 'utils/dataTableUtils';
import { DataTableFilter } from 'components/Common/DataTableFilters/DataTableFilter';
import { ColumnDefinition, DataTableWithId, HeadCellFilterType } from 'components/Common/DataTable/DataTableTypes';

type Props<T> = {
  data: any;
  headCells: ColumnDefinition<T>[];
  orderBy: string;
  order: Order;
  setOrder: Dispatch<SetStateAction<Order>>;
  setOrderBy: Dispatch<SetStateAction<string>>;
  handleSort: (selectedFilters: { [key: string]: string }[]) => void;
  handleSearch: (searchString: React.SetStateAction<string>, searchKey: keyof T) => void;
};

type IconComponentType = React.FunctionComponent<{ className: string }>;

const EnhancedTableHead = <T extends DataTableWithId>({
  data,
  headCells,
  orderBy,
  order,
  setOrder,
  setOrderBy,
  handleSort,
  handleSearch,
}: Props<T>) => {
  const onRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: string) => () => {
    onRequestSort(property);
  };

  const getIconComponent = (id: any) => {
    if (orderBy === id) {
      return order === 'asc' ? ColumnSortAscIcon : ColumnSortDescIcon;
    } else {
      return ColumnSortIcon;
    }
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          return (
            <TableCell
              key={headCell.label.toString()}
              sortDirection={orderBy === headCell.id ? order : false}
              id={`tablehead-${String(headCell.id)}`}
              style={{
                width: headCell.width,
                color: headCell.color,
                borderBottom: `none`,
              }}
            >
              {headCell.filter === HeadCellFilterType.SORT && (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id.toString())}
                  IconComponent={getIconComponent(headCell.id) as IconComponentType}
                >
                  {headCell.label}
                </TableSortLabel>
              )}
              {(headCell.filter === HeadCellFilterType.CHECKBOXFILTER ||
                headCell.filter === HeadCellFilterType.SEARCHFILTER) && (
                <DataTableFilter
                  data={data}
                  headCell={headCell}
                  handleSort={handleSort}
                  handleSearch={handleSearch}
                  shouldFormatEnum={headCell.shouldFormatEnum}
                />
              )}
              {headCell.filter === HeadCellFilterType.EMPTY && headCell.label}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default EnhancedTableHead;
